import { DefaultFilterTypeKey } from '@/core/collections/contracts-api/default-filter-types/default-filter-type.interface';
import type { LocationQuery, NavigationGuard } from 'vue-router';

export const REMEMBER_LAST_USED_FILTERS_SETTINGS_KEY = 'nxtpay-experimental-remember-last-used-filters';
export const REMEMBERED_QUERY_CONTRACTS_OVERVIEW_KEY = 'nxtpay-remembered-query-contracts-overview';
export const REMEMBERED_QUERY_PERFORMANCES_OVERVIEW_KEY = 'nxtpay-remembered-query-performances-overview';

export type RememberLastUsedFiltersPageKey = 'contractsOverview' | 'performancesOverview';
export type RememberLastUsedFiltersMode = 'off' | 'on';

export interface RememberLastUsedFiltersSettings {
   contractsOverview: RememberLastUsedFiltersMode;
   performancesOverview: RememberLastUsedFiltersMode;
}

export type SanitizedQuery = Record<string, string | string[]>;

export interface RememberedQuerySnapshot {
   query: SanitizedQuery;
   savedAt: string;
}

const DEFAULT_SETTINGS: RememberLastUsedFiltersSettings = {
   contractsOverview: 'off',
   performancesOverview: 'off',
};

const EXCLUDED_QUERY_KEYS = new Set([
   'page',
   'contract_process_state_id',
   'contract-process-state-id',
   'ep_contract_process_state_group',
   'etm_contract_process_state_group',
]);

const CONTRACTS_INCLUDE_KEYS = new Set([
   'from',
   'till',
   'employer_id',
   'team_id',
   'customer_id',
   'candidate_sync_id',
   'customer_group_id',
   'joint_committee_number',
   'group_by',
   'group_by[]',
   'cost_center_id',
   'worker_statute_id',
   'is_fictional',
   'trashed',
   'sort',
   'default_filter_id',
]);

const PERFORMANCES_INCLUDE_KEYS = new Set([
   'from',
   'till',
   'employer_id',
   'team_id',
   'customer_group_id',
   'customer_id',
   'candidate_sync_id',
   'cost_center_id',
   'easypay_contract_process_state_ids',
   'contract_process_state_ids',
   'has_unhandled_contract_process_events',
   'weekdays_to_hide',
   'weekdays_to_hide[]',
   'default_filter_id',
]);

function rememberedQueryStorageKey(page: RememberLastUsedFiltersPageKey): string {
   return page === 'contractsOverview' ? REMEMBERED_QUERY_CONTRACTS_OVERVIEW_KEY : REMEMBERED_QUERY_PERFORMANCES_OVERVIEW_KEY;
}

export function getDefaultFilterLocalStorageKey(page: RememberLastUsedFiltersPageKey): string {
   const typeKey = page === 'contractsOverview' ? DefaultFilterTypeKey.ContractsOverview : DefaultFilterTypeKey.PerformancesOverview;
   return `${typeKey}-default-filter`;
}

export function getRememberLastUsedFiltersSettings(): RememberLastUsedFiltersSettings {
   try {
      const raw = localStorage.getItem(REMEMBER_LAST_USED_FILTERS_SETTINGS_KEY);
      if (!raw) return { ...DEFAULT_SETTINGS };

      const parsed = JSON.parse(raw) as Partial<RememberLastUsedFiltersSettings>;
      return {
         contractsOverview: parsed.contractsOverview === 'on' ? 'on' : 'off',
         performancesOverview: parsed.performancesOverview === 'on' ? 'on' : 'off',
      };
   } catch {
      return { ...DEFAULT_SETTINGS };
   }
}

export function setRememberLastUsedFiltersSettings(settings: RememberLastUsedFiltersSettings): void {
   localStorage.setItem(REMEMBER_LAST_USED_FILTERS_SETTINGS_KEY, JSON.stringify(settings));
}

export function isRememberLastUsedFiltersModeOn(page: RememberLastUsedFiltersPageKey): boolean {
   return getRememberLastUsedFiltersSettings()[page] === 'on';
}

function locationQueryToRecord(query: LocationQuery): SanitizedQuery {
   const result: SanitizedQuery = {};

   for (const [key, value] of Object.entries(query)) {
      if (value == null || value === '') continue;

      if (Array.isArray(value)) {
         const filtered = value.filter((entry): entry is string => entry != null && entry !== '');
         if (filtered.length) result[key] = filtered;
         continue;
      }

      result[key] = value;
   }

   return result;
}

function isIncludedKey(key: string, includeKeys: Set<string>): boolean {
   return includeKeys.has(key);
}

function normalizeRememberedContractsGroupBy(query: SanitizedQuery): void {
   if (query['group_by[]'] != null) {
      delete query.group_by;
      return;
   }

   if (query.group_by == null) return;

   query['group_by[]'] = query.group_by;
   delete query.group_by;
}

export function sanitizeRememberedQuery(page: RememberLastUsedFiltersPageKey, query: LocationQuery | SanitizedQuery): SanitizedQuery {
   const includeKeys = page === 'contractsOverview' ? CONTRACTS_INCLUDE_KEYS : PERFORMANCES_INCLUDE_KEYS;
   const record = locationQueryToRecord(query as LocationQuery);
   const sanitized: SanitizedQuery = {};

   for (const [key, value] of Object.entries(record)) {
      if (EXCLUDED_QUERY_KEYS.has(key)) continue;
      if (!isIncludedKey(key, includeKeys)) continue;
      sanitized[key] = value;
   }

   if (page === 'contractsOverview') {
      normalizeRememberedContractsGroupBy(sanitized);
   }

   return sanitized;
}

export function getRememberedQuery(page: RememberLastUsedFiltersPageKey): SanitizedQuery | null {
   try {
      const raw = localStorage.getItem(rememberedQueryStorageKey(page));
      if (!raw) return null;

      const parsed = JSON.parse(raw) as RememberedQuerySnapshot;
      if (!parsed?.query || typeof parsed.query !== 'object') {
         clearRememberedQuery(page);
         return null;
      }

      return sanitizeRememberedQuery(page, parsed.query);
   } catch {
      clearRememberedQuery(page);
      return null;
   }
}

export function setRememberedQuery(page: RememberLastUsedFiltersPageKey, query: LocationQuery | SanitizedQuery): void {
   const sanitized = sanitizeRememberedQuery(page, query);
   if (Object.keys(sanitized).length === 0) {
      clearRememberedQuery(page);
      return;
   }

   const snapshot: RememberedQuerySnapshot = {
      query: sanitized,
      savedAt: new Date().toISOString(),
   };

   localStorage.setItem(rememberedQueryStorageKey(page), JSON.stringify(snapshot));
}

export function clearRememberedQuery(page: RememberLastUsedFiltersPageKey): void {
   localStorage.removeItem(rememberedQueryStorageKey(page));
}

export function createOverviewQueryRestoreGuard(page: RememberLastUsedFiltersPageKey): NavigationGuard {
   const defaultFilterLocalStorageKey = getDefaultFilterLocalStorageKey(page);

   return (to, _from, next) => {
      if (Object.keys(to.query).length > 0) {
         return next();
      }

      if (isRememberLastUsedFiltersModeOn(page)) {
         const remembered = getRememberedQuery(page);
         if (remembered && Object.keys(remembered).length > 0) {
            return next({ ...to, query: remembered });
         }
      }

      const savedFilterQuery = localStorage.getItem(defaultFilterLocalStorageKey);
      if (savedFilterQuery) {
         try {
            return next({ ...to, query: JSON.parse(savedFilterQuery) });
         } catch {
            localStorage.removeItem(defaultFilterLocalStorageKey);
         }
      }

      return next();
   };
}

export function skipDefaultFilterGuardsWhenRememberModeOn(page: RememberLastUsedFiltersPageKey, guard: NavigationGuard): NavigationGuard {
   return (to, from, next) => {
      if (isRememberLastUsedFiltersModeOn(page)) return next();
      return guard(to, from, next);
   };
}
